/**
 * AdvancedOptionsSection Component
 *
 * A collapsible section for configuring advanced QR code options.
 * When enabled, reveals options for experimental features, language override,
 * swing capture, and game mode settings.
 *
 * Requirements:
 * - 1.2: Section collapsed by default (controlled by enabled prop)
 * - 1.3: When toggle disabled, hide all options
 * - 1.4: When toggle enabled, reveal all options
 * - 1.5: Display warning icon next to toggle label
 * - 1.6: Tooltip on warning icon with advanced user warning
 * - 2.1, 2.5: Experimental Features toggle with label and description
 * - 3.1, 3.6: Language Override dropdown with label and description
 * - 4.1, 4.5: Swing Capture toggle with label and description
 * - 5.1, 5.7: Game Mode text input with label and description
 */

import { useCallback, useId, useState } from 'react';
import type { AdvancedOptions, LanguageCode } from '../types/advancedOptions';
import { LANGUAGE_OPTIONS } from '../types/advancedOptions';
import { validateGameMode } from '../utils/gameModeValidation';
import './AdvancedOptionsSection.css';

/**
 * Props for the AdvancedOptionsSection component
 */
export interface AdvancedOptionsSectionProps {
  /** Current advanced options configuration */
  readonly advancedOptions: AdvancedOptions;
  /** Callback fired when any option changes */
  readonly onOptionsChange: (options: Partial<AdvancedOptions>) => void;
}

/**
 * Warning tooltip message for advanced options
 * Requirement 1.6: Tooltip message for advanced users
 */
const WARNING_TOOLTIP_MESSAGE =
  'These settings are for advanced users. Only modify if you understand their effects.';

/**
 * AdvancedOptionsSection - Collapsible section for advanced QR options
 *
 * Provides a toggle to enable/disable advanced options, with a warning icon
 * and tooltip. When enabled, displays controls for:
 * - Experimental Features (toggle)
 * - Language Override (dropdown)
 * - Swing Capture (toggle)
 * - Game Mode (text input with validation)
 *
 * @example
 * ```tsx
 * <AdvancedOptionsSection
 *   advancedOptions={advancedOptions}
 *   onOptionsChange={(updates) => updateAdvancedOptions(updates)}
 * />
 * ```
 */
export function AdvancedOptionsSection({
  advancedOptions,
  onOptionsChange,
}: AdvancedOptionsSectionProps): JSX.Element {
  const mainToggleId = useId();
  const experimentalId = useId();
  const languageId = useId();
  const swingCaptureId = useId();
  const gameModeId = useId();

  // Tooltip visibility state
  const [showTooltip, setShowTooltip] = useState(false);

  // Main toggle handler
  const handleEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onOptionsChange({ enabled: event.target.checked });
    },
    [onOptionsChange]
  );

  // Experimental features toggle handler
  const handleExperimentalChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onOptionsChange({ experimentalFeatures: event.target.checked });
    },
    [onOptionsChange]
  );

  // Language override dropdown handler
  const handleLanguageChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      onOptionsChange({
        languageOverride: value === '' ? null : (value as LanguageCode),
      });
    },
    [onOptionsChange]
  );

  // Swing capture toggle handler
  const handleSwingCaptureChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onOptionsChange({ swingCapture: event.target.checked });
    },
    [onOptionsChange]
  );

  // Game mode input handler with validation
  // Requirement 5.3, 5.4: Validate and sanitize game mode input
  const handleGameModeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { sanitized } = validateGameMode(event.target.value);
      onOptionsChange({ gameMode: sanitized });
    },
    [onOptionsChange]
  );

  // Tooltip handlers
  const handleTooltipShow = useCallback(() => {
    setShowTooltip(true);
  }, []);

  const handleTooltipHide = useCallback(() => {
    setShowTooltip(false);
  }, []);

  return (
    <div className="advanced-options-section">
      <div className="advanced-options-section__header">
        <label className="advanced-options-section__toggle-container" htmlFor={mainToggleId}>
          <span className="advanced-options-section__label">
            Advanced Options
            {/* Requirement 1.5: Warning icon next to toggle label */}
            <span
              className="advanced-options-section__warning-icon"
              onMouseEnter={handleTooltipShow}
              onMouseLeave={handleTooltipHide}
              onFocus={handleTooltipShow}
              onBlur={handleTooltipHide}
              tabIndex={0}
              role="img"
              aria-label="Warning: advanced settings"
            >
              ⚠️
              {/* Requirement 1.6: Tooltip on warning icon */}
              {showTooltip && (
                <span className="advanced-options-section__tooltip" role="tooltip">
                  {WARNING_TOOLTIP_MESSAGE}
                </span>
              )}
            </span>
          </span>
          <div className="advanced-options-section__switch-wrapper">
            <input
              id={mainToggleId}
              type="checkbox"
              className="advanced-options-section__input"
              checked={advancedOptions.enabled}
              onChange={handleEnabledChange}
              role="switch"
              aria-checked={advancedOptions.enabled}
            />
            <span className="advanced-options-section__switch" aria-hidden="true">
              <span className="advanced-options-section__switch-thumb" />
            </span>
          </div>
        </label>
      </div>

      {/* Requirement 1.3, 1.4: Show/hide options based on enabled state */}
      {advancedOptions.enabled && (
        <div className="advanced-options-section__options">
          {/* Requirement 2.1, 2.5: Experimental Features toggle */}
          <div className="advanced-options-section__option">
            <label className="advanced-options-section__option-toggle" htmlFor={experimentalId}>
              <input
                id={experimentalId}
                type="checkbox"
                className="advanced-options-section__checkbox"
                checked={advancedOptions.experimentalFeatures}
                onChange={handleExperimentalChange}
              />
              <span className="advanced-options-section__option-label">Experimental Features</span>
            </label>
            <p className="advanced-options-section__option-description">
              Enable experimental features
            </p>
          </div>

          {/* Requirement 3.1, 3.6: Language Override dropdown */}
          <div className="advanced-options-section__option">
            <label className="advanced-options-section__option-label" htmlFor={languageId}>
              Language Override
            </label>
            <select
              id={languageId}
              className="advanced-options-section__select"
              value={advancedOptions.languageOverride ?? ''}
              onChange={handleLanguageChange}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value ?? 'none'} value={option.value ?? ''}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="advanced-options-section__option-description">
              Force a specific language
            </p>
          </div>

          {/* Requirement 4.1, 4.5: Swing Capture toggle */}
          <div className="advanced-options-section__option">
            <label className="advanced-options-section__option-toggle" htmlFor={swingCaptureId}>
              <input
                id={swingCaptureId}
                type="checkbox"
                className="advanced-options-section__checkbox"
                checked={advancedOptions.swingCapture}
                onChange={handleSwingCaptureChange}
              />
              <span className="advanced-options-section__option-label">Swing Capture</span>
            </label>
            <p className="advanced-options-section__option-description">Enable Swing Capture</p>
          </div>

          {/* Requirement 5.1, 5.7: Game Mode text input */}
          <div className="advanced-options-section__option">
            <label className="advanced-options-section__option-label" htmlFor={gameModeId}>
              Game Mode
            </label>
            <input
              id={gameModeId}
              type="text"
              className="advanced-options-section__text-input"
              value={advancedOptions.gameMode}
              onChange={handleGameModeChange}
              placeholder="e.g., whats_in_my_bag"
              maxLength={20}
            />
            <p className="advanced-options-section__option-description">Open a specific game mode</p>
          </div>
        </div>
      )}
    </div>
  );
}
